import React, { useState } from 'react';
import JSZip from 'jszip';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import centroid from '@turf/centroid';

import ArquivoInput from './arquivoInput';
import {
    baixarBlob, identificar, kml, lerShapeZip,
    nomeBase, nomeSeguro, shapeZip
} from './ferramentasUtils';

const botao = {
    border: 0,
    borderRadius: 9,
    padding: '11px 18px',
    background: '#44942c',
    color: '#fff',
    fontWeight: 700
};

const copiarShape = async (destino, buffer, prefixo) => {
    const origem = await JSZip.loadAsync(buffer);
    const entradas = Object.values(origem.files).filter(item => !item.dir);
    for (const entrada of entradas) {
        const extensao = entrada.name.split('.').pop().toLowerCase();
        if (['shp', 'shx', 'dbf', 'prj'].includes(extensao)) {
            destino.file(`${prefixo}.${extensao}`, await entrada.async('arraybuffer'));
        }
    }
};

export const ConverterColeta = () => {
    const [zonas, setZonas] = useState([]);
    const [pontos, setPontos] = useState([]);
    const [status, setStatus] = useState('Selecione os arquivos.');
    const [processando, setProcessando] = useState(false);

    const executar = async () => {
        try {
            setProcessando(true);
            const porNome = arquivos => arquivos.reduce((mapa, arquivo) => ({
                ...mapa,
                [nomeBase(arquivo.name).toUpperCase()]: arquivo
            }), {});
            const zonasMapa = porNome(zonas);
            const pontosMapa = porNome(pontos);
            const nomes = [...new Set([
                ...Object.keys(zonasMapa), ...Object.keys(pontosMapa)
            ])];
            const faltantes = nomes.filter(nome =>
                !zonasMapa[nome] || !pontosMapa[nome]
            );
            if (faltantes.length) {
                throw new Error(
                    `Faltam pares de zonas/pontos: ${faltantes.join(', ')}.`
                );
            }
            if (!nomes.length) throw new Error('Selecione ao menos uma área.');

            const pacote = new JSZip();
            for (let indice = 0; indice < nomes.length; indice += 1) {
                const nome = nomes[indice];
                setStatus(`Processando ${indice + 1} de ${nomes.length}: ${nome}`);
                const [colecaoZonas, colecaoPontos] = await Promise.all([
                    lerShapeZip(zonasMapa[nome]),
                    lerShapeZip(pontosMapa[nome])
                ]);
                const poligonos = colecaoZonas.features.filter(item =>
                    ['Polygon', 'MultiPolygon'].includes(item.geometry.type)
                );
                const pontosValidos = [];
                const erros = [];
                colecaoPontos.features.filter(item =>
                    item.geometry.type === 'Point'
                ).forEach((ponto, posicao) => {
                    const correspondentes = poligonos.filter(poligono =>
                        booleanPointInPolygon(ponto, poligono)
                    );
                    if (correspondentes.length !== 1) {
                        erros.push(posicao + 1);
                    } else {
                        pontosValidos.push({
                            ...ponto,
                            properties: { id: pontosValidos.length + 1 }
                        });
                    }
                });
                if (erros.length) {
                    throw new Error(
                        `${nome}: ${erros.length} ponto(s) fora das zonas ou em sobreposição.`
                    );
                }
                const zonasSemPonto = poligonos.filter(poligono =>
                    !pontosValidos.some(ponto =>
                        booleanPointInPolygon(ponto, poligono)
                    )
                );
                if (zonasSemPonto.length) {
                    throw new Error(`${nome}: ${zonasSemPonto.length} zona(s) sem ponto.`);
                }
                const areaZip = new JSZip();
                await copiarShape(areaZip, await shapeZip({
                    type: 'FeatureCollection', features: pontosValidos
                }, 'amostras', 'amostras'), 'amostras');
                await copiarShape(areaZip, await shapeZip({
                    type: 'FeatureCollection',
                    features: poligonos.map(item => ({
                        ...item, properties: {}
                    }))
                }, 'contorno', 'contorno'), 'contorno');
                pacote.file(
                    `${nomeSeguro(nome)}_contorno.zip`,
                    await areaZip.generateAsync({ type: 'arraybuffer' })
                );
            }
            baixarBlob(
                await pacote.generateAsync({ type: 'blob', compression: 'DEFLATE' }),
                nomes.length === 1 ? `${nomeSeguro(nomes[0])}_coleta.zip` : 'coletas_prontas.zip'
            );
            setStatus(`${nomes.length} área(s) processada(s) com sucesso.`);
        } catch (ex) {
            setStatus(ex.message);
        } finally {
            setProcessando(false);
        }
    };

    return <div>
        <p>Combine os ZIPs de zonas e pontos pelo nome da área.</p>
        <div className='row'>
            <div className='col-md-6'><ArquivoInput label='ZIPs das zonas'
                accept='.zip' multiple onChange={setZonas}
                ajuda={`${zonas.length} arquivo(s) selecionado(s)`} /></div>
            <div className='col-md-6'><ArquivoInput label='ZIPs dos pontos'
                accept='.zip' multiple onChange={setPontos}
                ajuda={`${pontos.length} arquivo(s) selecionado(s)`} /></div>
        </div>
        <button style={botao} disabled={processando} onClick={executar}>
            {processando ? 'Processando…' : 'Gerar arquivos por área'}
        </button>
        <div className='alert alert-light mt-3'>{status}</div>
    </div>;
};

export const PontosZonas = () => {
    const [zona, setZona] = useState(null);
    const [ponto, setPonto] = useState(null);
    const [status, setStatus] = useState('Selecione os dois ZIPs.');
    const executar = async () => {
        try {
            if (!zona || !ponto) throw new Error('Selecione zonas e pontos.');
            const [zonas, pontos] = await Promise.all([
                lerShapeZip(zona), lerShapeZip(ponto)
            ]);
            let corrigidas = 0;
            const features = zonas.features.map(poligono => {
                const referencia = pontos.features.find(item =>
                    item.geometry.type === 'Point' &&
                    booleanPointInPolygon(item, poligono)
                );
                if (!referencia) return poligono;
                const id = identificar(referencia.properties);
                if (id !== identificar(poligono.properties)) corrigidas += 1;
                return { ...poligono, properties: { ...poligono.properties, ID: id } };
            });
            const buffer = await shapeZip({
                type: 'FeatureCollection', features
            }, 'zonas', 'zonas_corrigidas');
            baixarBlob(new Blob([buffer]), 'zonas_corrigidas.zip');
            setStatus(`${corrigidas} ID(s) corrigida(s).`);
        } catch (ex) {
            setStatus(ex.message);
        }
    };
    return <div><div className='row'>
        <div className='col-md-6'><ArquivoInput label='ZIP das zonas' accept='.zip'
            onChange={arquivos => setZona(arquivos[0])} /></div>
        <div className='col-md-6'><ArquivoInput label='ZIP dos pontos' accept='.zip'
            onChange={arquivos => setPonto(arquivos[0])} /></div>
    </div><button style={botao} onClick={executar}>Conferir e corrigir</button>
        <div className='alert alert-light mt-3'>{status}</div></div>;
};

export const MapsGsi = () => {
    const [arquivos, setArquivos] = useState([]);
    const [status, setStatus] = useState('Selecione as áreas.');
    const executar = async () => {
        try {
            if (!arquivos.length) throw new Error('Selecione ao menos uma área.');
            const zip = new JSZip();
            for (const arquivo of arquivos) {
                const colecao = await lerShapeZip(arquivo);
                zip.file(`${nomeSeguro(nomeBase(arquivo.name))}.kml`, kml(colecao));
            }
            baixarBlob(await zip.generateAsync({ type: 'blob' }), 'Maps_GSI_Areas.zip');
            setStatus(`${arquivos.length} área(s) convertida(s).`);
        } catch (ex) {
            setStatus(ex.message);
        }
    };
    return <div><ArquivoInput label='ZIPs das áreas' accept='.zip' multiple
        onChange={setArquivos} ajuda='Cada ZIP deve conter SHP, SHX, DBF e PRJ.' />
        <button style={botao} onClick={executar}>Converter áreas para KML</button>
        <div className='alert alert-light mt-3'>{status}</div></div>;
};

export const AlterarIds = () => {
    const [arquivo, setArquivo] = useState(null);
    const [campo, setCampo] = useState('ID');
    const [inicio, setInicio] = useState(1);
    const [status, setStatus] = useState('Selecione o arquivo de zonas.');
    const executar = async () => {
        try {
            if (!arquivo) throw new Error('Selecione um arquivo ZIP.');
            const colecao = await lerShapeZip(arquivo);
            const features = colecao.features.map((item, indice) => ({
                ...item,
                properties: {
                    ...item.properties,
                    [campo.trim() || 'ID']: Number(inicio || 1) + indice
                }
            }));
            const buffer = await shapeZip({ type: 'FeatureCollection', features },
                'zonas', `${nomeSeguro(nomeBase(arquivo.name))}_ids`);
            baixarBlob(new Blob([buffer]), `${nomeSeguro(nomeBase(arquivo.name))}_ids.zip`);
            setStatus(`${features.length} elemento(s) numerado(s) com sucesso.`);
        } catch (ex) {
            setStatus(ex.message);
        }
    };
    return <div>
        <ArquivoInput label='ZIP das zonas' accept='.zip'
            onChange={arquivos => setArquivo(arquivos[0])} />
        <div className='row'>
            <div className='form-group col-md-6'><label>Nome do campo</label>
                <input className='form-control' value={campo}
                    onChange={e => setCampo(e.target.value)} /></div>
            <div className='form-group col-md-6'><label>Primeiro ID</label>
                <input className='form-control' type='number' step='1' value={inicio}
                    onChange={e => setInicio(e.target.value)} /></div>
        </div>
        <button style={botao} onClick={executar}>Gerar zonas numeradas</button>
        <div className='alert alert-light mt-3'>{status}</div>
    </div>;
};

const ClassificarPivo = ({ tituloBotao, nomeSaida, campo }) => {
    const [zonasArquivo, setZonasArquivo] = useState(null);
    const [pivosArquivo, setPivosArquivo] = useState(null);
    const [status, setStatus] = useState('Selecione as zonas e os pivôs.');
    const executar = async () => {
        try {
            if (!zonasArquivo || !pivosArquivo) {
                throw new Error('Selecione os dois arquivos ZIP.');
            }
            const [zonas, pivos] = await Promise.all([
                lerShapeZip(zonasArquivo), lerShapeZip(pivosArquivo)
            ]);
            let irrigadas = 0;
            const features = zonas.features.map(zona => {
                const centro = centroid(zona);
                const dentro = pivos.features.some(pivo =>
                    ['Polygon', 'MultiPolygon'].includes(pivo.geometry.type) &&
                    booleanPointInPolygon(centro, pivo)
                );
                if (dentro) irrigadas += 1;
                return {
                    ...zona,
                    properties: {
                        ...zona.properties,
                        [campo]: dentro ? 'IRRIGADA' : 'SEQUEIRO'
                    }
                };
            });
            const buffer = await shapeZip({ type: 'FeatureCollection', features },
                'zonas', nomeSaida);
            baixarBlob(new Blob([buffer]), `${nomeSaida}.zip`);
            setStatus(`${irrigadas} zona(s) irrigada(s) e ${features.length - irrigadas} de sequeiro.`);
        } catch (ex) { setStatus(ex.message); }
    };
    return <div>
        <p>A classificação usa a posição central de cada zona em relação aos pivôs.</p>
        <div className='row'>
            <div className='col-md-6'><ArquivoInput label='ZIP das zonas' accept='.zip'
                onChange={arquivos => setZonasArquivo(arquivos[0])} /></div>
            <div className='col-md-6'><ArquivoInput label='ZIP dos pivôs' accept='.zip'
                onChange={arquivos => setPivosArquivo(arquivos[0])} /></div>
        </div>
        <button style={botao} onClick={executar}>{tituloBotao}</button>
        <div className='alert alert-light mt-3'>{status}</div>
    </div>;
};

export const AptidaoIrrigacao = () => <ClassificarPivo
    tituloBotao='Classificar aptidão' nomeSaida='aptidao_irrigacao' campo='APTIDAO' />;

export const PivoEmZonas = () => <ClassificarPivo
    tituloBotao='Incluir pivô nas zonas' nomeSaida='zonas_com_pivo' campo='SISTEMA' />;
